import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingDocumentListItem,
  DocumentCraftingListSort,
} from "../../../Models/documentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import LabelChips from "../Common/LabelChips";
import {
  DocumentOwner,
  DocumentStatus,
  DocumentThumbnail,
  DocumentTypeLine,
  LatestRevision,
  documentTarget,
  isInteractiveTarget,
} from "./DocumentListParts";
import { SortOrder } from "./useDocumentListParams";

interface Column {
  key: string;
  label: string;
  sort?: DocumentCraftingListSort;
  width?: number | string;
}

const headerSx = {
  fontSize: 12,
  fontWeight: 700,
  color: color.gray500,
  whiteSpace: "nowrap",
};

/** The documents as a table (md and wider); server-side sort per column. */
const DocumentTable: React.FC<{
  documents: DocumentCraftingDocumentListItem[];
  sort: DocumentCraftingListSort;
  order: SortOrder;
  onSort: (column: DocumentCraftingListSort) => void;
}> = ({ documents, sort, order, onSort }) => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const { primary } = useDashboardColors();

  const columns: Column[] = [
    { key: "thumbnail", label: "", width: 88 },
    { key: "title", label: t("list.columns.title"), sort: "title" },
    { key: "type", label: t("list.columns.type"), sort: "type", width: 190 },
    { key: "owner", label: t("list.columns.owner"), width: 200 },
    {
      key: "revision",
      label: t("list.columns.latestRevision"),
      sort: "last_revision",
      width: 190,
    },
    { key: "status", label: t("list.columns.status"), width: 190 },
  ];

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 900, tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            {columns.map(({ key, label, sort: columnSort, width }) => (
              <TableCell
                key={key}
                sx={{ ...headerSx, width }}
                sortDirection={columnSort === sort ? order : false}
              >
                {columnSort ? (
                  <TableSortLabel
                    active={columnSort === sort}
                    direction={columnSort === sort ? order : "asc"}
                    onClick={() => onSort(columnSort)}
                  >
                    {label}
                  </TableSortLabel>
                ) : (
                  label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((item) => {
            const target = documentTarget(item);
            return (
              <TableRow
                key={item.id}
                hover
                onClick={(event) => {
                  if (!isInteractiveTarget(event.target)) navigate(target);
                }}
                sx={{
                  cursor: "pointer",
                  "& > td": { py: 1.25, verticalAlign: "middle" },
                }}
              >
                <TableCell>
                  <DocumentThumbnail item={item} />
                </TableCell>
                <TableCell sx={{ minWidth: 0 }}>
                  <Link
                    component={RouterLink}
                    to={target}
                    underline="hover"
                    sx={{
                      display: "block",
                      fontSize: 14,
                      fontWeight: 700,
                      color: primary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.title}
                  >
                    {item.title}
                  </Link>
                  {(item.project_labels.length > 0 ||
                    item.personal_labels.length > 0) && (
                    <Box sx={{ mt: 0.75 }}>
                      <LabelChips
                        projectLabels={item.project_labels}
                        personalLabels={item.personal_labels}
                        max={3}
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <DocumentTypeLine item={item} />
                </TableCell>
                <TableCell>
                  <DocumentOwner item={item} />
                </TableCell>
                <TableCell>
                  <LatestRevision item={item} />
                </TableCell>
                <TableCell>
                  <DocumentStatus item={item} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default DocumentTable;
